import { type ReactNode, useState, type RefObject } from "react";
import { ArrowRight, Loader2, Plus } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { FieldError } from "@/shared/components/ui/FieldError";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { AcceptDeclineActions } from "@/shared/components/EstateItems/AcceptDeclineActions";
import type { EstateItem } from "@/shared/types/estateTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import { EmptyState } from "@/shared/components/ui/EmptyState";

import { AccountRow } from "./AccountRow";
import { buildItemSubtitle } from "./itemSubtitles";

interface EditingState<F> {
  id: number;
  form: F;
  existingFileName: string | null;
  fileRemoved: boolean;
}

interface ReviewScreenLayoutProps<F> {
  items: EstateItem[];
  isLoading: boolean;
  isFetching?: boolean;
  isRedirecting?: boolean;
  screenTitle?: string;
  screenSubtitle?: string;
  itemLabel: string;
  listTitle: string;
  addButtonLabel?: string;
  onAddAnother: () => void;
  onContinue: () => void;
  renderSubtitle?: (item: EstateItem) => string;
  renderItem?: (item: EstateItem, defaultRow: ReactNode) => ReactNode;
  editDialogTitle: string;
  editDialogRef?: RefObject<HTMLDivElement | null>;
  parseItemToForm: (item: EstateItem) => F;
  renderEditForm: (props: {
    form: F;
    onChange: (form: F) => void;
    existingFileName: string | null;
    onFileRemove: () => void;
  }) => ReactNode;
  onSaveEdit: (editing: EditingState<F>) => Promise<void>;
  isUpdatingItem: boolean;
  deleteItem: (id: number) => Promise<void>;
  deleteDialogTitle: string;
  disclaimer?: string;
  itemActions?: {
    handleAccept: (id: number) => Promise<boolean> | void;
    handleDecline: (id: number) => Promise<boolean> | void;
    getItemLoadingAction: (id: number) => "accept" | "decline" | undefined;
  };
}

export function ReviewScreenLayout<F>({
  items,
  isLoading,
  isFetching,
  isRedirecting,
  screenTitle,
  screenSubtitle,
  itemLabel,
  listTitle,
  onAddAnother,
  onContinue,
  renderSubtitle,
  renderItem,
  editDialogTitle,
  editDialogRef,
  parseItemToForm,
  renderEditForm,
  onSaveEdit,
  isUpdatingItem,
  deleteItem,
  deleteDialogTitle,
  disclaimer,
  itemActions,
}: ReviewScreenLayoutProps<F>) {
  const [editing, setEditing] = useState<EditingState<F> | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<EstateItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);

  const handleEdit = (item: EstateItem) => {
    setEditError(null);
    setEditing({
      id: item.id,
      form: parseItemToForm(item),
      existingFileName: item.source_document?.file_name ?? null,
      fileRemoved: false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      await onSaveEdit(editing);
      setEditing(null);
    } catch (e) {
      if (e instanceof Error) {
        setEditError(e.message);
      }
      sentryUtils.captureError(e, "ReviewScreen save edit failed");
    }
  };

  const pendingItems = items.filter((i) => i.extraction_status === "pending");

  const handleAcceptAll = async () => {
    if (!itemActions || pendingItems.length === 0) return;
    setIsAcceptingAll(true);
    try {
      const results = await Promise.allSettled(
        pendingItems.map((i) => itemActions.handleAccept(i.id)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        setEditError(
          `${failed} item${failed === 1 ? "" : "s"} couldn't be accepted. Please try again.`,
        );
      }
    } catch {
      setEditError("Failed to accept items. Please try again.");
    } finally {
      setIsAcceptingAll(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteItem(itemToDelete.id);
      setItemToDelete(null);
      setEditing(null);
    } catch {
      // deletion failed
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading || isRedirecting || (isFetching && items.length === 0)) {
    return (
      <div className="mx-auto flex w-full max-w-2xl items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
      {screenTitle && <h2 className="text-center">{screenTitle}</h2>}
      {screenSubtitle && (
        <p className="mt-2 text-center text-primary text-pretty">{screenSubtitle}</p>
      )}
      <div className="rounded-lg border border-primary bg-white p-6">
        <div className="flex flex-col gap-6">
          {items.length === 0 && (
            <EmptyState
              title={`No ${itemLabel} added yet`}
              actionLabel={`Add ${itemLabel}`}
              onAction={onAddAnother}
            />
          )}

          {items.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold">{listTitle}</p>
                <button
                  type="button"
                  onClick={onAddAnother}
                  className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
              {items.map((item) => {
                const row = (
                  <AccountRow
                    key={item.id}
                    item={item}
                    subtitle={
                      (renderSubtitle ? renderSubtitle(item) : buildItemSubtitle(item)) || undefined
                    }
                    onEdit={handleEdit}
                    actions={
                      itemActions && item.extraction_status === "pending" ? (
                        <AcceptDeclineActions
                          onAccept={() => itemActions.handleAccept(item.id)}
                          onDecline={() => itemActions.handleDecline(item.id)}
                          loadingAction={itemActions.getItemLoadingAction(item.id)}
                        />
                      ) : undefined
                    }
                  />
                );
                return renderItem ? renderItem(item, row) : row;
              })}
            </div>
          )}
        </div>

        {disclaimer && <p className="text-center text-2xs text-tertiary">{disclaimer}</p>}
      </div>

      <div className="mt-7 flex flex-col items-center">
        {itemActions && pendingItems.length > 0 ? (
          <Button
            variant="brand"
            rounded={true}
            onClick={handleAcceptAll}
            disabled={isAcceptingAll}
            className="w-full"
          >
            {isAcceptingAll ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Accept all
              </>
            ) : (
              <>
                Accept all <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        ) : (
          <Button variant="brand" rounded={true} onClick={onContinue} className="w-full">
            Continue <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        <DialogContent
          ref={editDialogRef}
          size="lg"
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest("[data-address-dropdown]")) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            const related = e.detail.originalEvent.relatedTarget as HTMLElement | null;
            if (related?.closest("[data-address-dropdown]")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{editDialogTitle}</DialogTitle>
          </DialogHeader>
          {editing && (
            <DialogBody>
              {renderEditForm({
                form: editing.form,
                onChange: (form) => {
                  setEditError(null);
                  setEditing((prev) => (prev ? { ...prev, form } : null));
                },
                existingFileName: editing.fileRemoved ? null : editing.existingFileName,
                onFileRemove: () =>
                  setEditing((prev) => (prev ? { ...prev, fileRemoved: true } : null)),
              })}
              {editError && <FieldError message={editError} />}
            </DialogBody>
          )}
          <div className="flex justify-between pt-6">
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting || isUpdatingItem}
              onClick={() => {
                setItemToDelete(items.find((i) => i.id === editing?.id) ?? null);
              }}
            >
              Delete
            </Button>
            <Button disabled={isUpdatingItem || isDeleting} onClick={handleSaveEdit}>
              {isUpdatingItem ? "Saving..." : "Update"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={deleteDialogTitle}
        description={`Are you sure you want to delete "${itemToDelete?.item_name}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
}
